import React, { useState, useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import MetricSelector from '../selectors/MetricSelector';
import YearSelector from './YearSelector';
import IndustryMetricSelector from '../selectors/IndustryMetricSelector';

export const LaborForceAndUnemploymentChart = ({ data = [] }) => {
  const [selectedMetric, setSelectedMetric] = useState('unemployment'); // Default to 'unemployment'

  // Transform the raw data into the format needed for the chart
  const chartData = useMemo(() => {
    const grouped = {};

    data.forEach(item => {
      const { month, region, value, metric } = item;
      if (!grouped[month]) {
        grouped[month] = { month };
      }

      // Normalize metric names
      const normalizedMetric = metric.toLowerCase().replace(' rate', '');

      // Create keys like US_laborforce, CT_laborforce, US_unemployment, CT_unemployment
      const key = `${region}_${normalizedMetric}`;
      grouped[month][key] = value;
    });

    return Object.values(grouped)
      .sort((a, b) => new Date(a.month) - new Date(b.month))
      .slice(-5 * 12); // Show last 5 years of monthly data
  }, [data]);

  // Function to parse the date string correctly
  const parseDate = (dateString) => {
    const [year, month] = dateString.split('-');
    return new Date(year, month - 1); // month - 1 because Date expects 0-based month index
  };

  return (
    <div>
      <MetricSelector selectedMetric={selectedMetric} setSelectedMetric={setSelectedMetric} />
      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="month"
            tickFormatter={(tick) => {
              const date = parseDate(tick);
              return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
            }}
          />
          <YAxis />
          <Tooltip
            labelFormatter={(label) => {
              const date = parseDate(label);
              return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
            }}
          />
          <Legend />
          {selectedMetric === 'laborforce' && (
            <>
              <Line
                type="monotone"
                dataKey="US_laborforce"
                stroke="#8884d8"
                name="US Labor Force Rate"
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="CT_laborforce"
                stroke="#82ca9d"
                name="CT Labor Force Rate"
                dot={false}
              />
            </>
          )}
          {selectedMetric === 'unemployment' && (
            <>
              <Line
                type="monotone"
                dataKey="US_unemployment"
                stroke="#ff7300"
                name="US Unemployment Rate"
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="CT_unemployment"
                stroke="#ff99cc"
                name="CT Unemployment Rate"
                dot={false}
              />
            </>
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};


export const IndustryJobsChart = ({ data = [] }) => {
  // Change default to months (60 months = 5 years)
  const [timeFrame, setTimeFrame] = useState(60);
  const [selectedView, setSelectedView] = useState('total');

// Calculate indexed values
const indexedData = useMemo(() => {
  if (data.length === 0) return [];

  // Group data by month
  const monthGroups = data.reduce((acc, item) => {
    if (!acc[item.month]) {
      acc[item.month] = {};
    }
    acc[item.month][item.industry] = item.value;
    return acc;
  }, {});

  // Convert to array and sort by month
  const sortedMonths = Object.keys(monthGroups).sort();
  
  // Need at least 2 months of data for any comparison
  if (sortedMonths.length < 2) return [];

  // Include an additional baseline month for proper indexing
  const displayMonths = timeFrame + 1;
  
  // Calculate the starting point for the timeframe
  const baseIndex = Math.max(0, sortedMonths.length - displayMonths);
  
  // Always use the first month in our range as the baseline
  const baselineMonth = sortedMonths[baseIndex];
  const baselineValues = monthGroups[baselineMonth];

  // Create indexed data points
  return sortedMonths.slice(baseIndex).map(month => {
    const monthData = { month };
    Object.entries(monthGroups[month]).forEach(([industry, value]) => {
      const baseValue = baselineValues[industry] || 1;
      monthData[industry] = Number(((value / baseValue) * 100).toFixed(1));
    });
    return monthData;
    });
  }, [data, timeFrame]);

  // Get filtered industries based on selected view
  const filteredIndustries = useMemo(() => {
    if (indexedData.length === 0) return [];
    const firstDataPoint = indexedData[0];
    const allIndustries = Object.keys(firstDataPoint).filter(key => key !== 'month');

    switch (selectedView) {
      case 'total':
        return allIndustries.filter(industry => 
          industry === 'Total_Nonfarm'
        );
      case 'sectors':
        return allIndustries.filter(industry => 
          industry === 'Total_Private' || industry === 'Government'
        );
      case 'industries':
        return allIndustries.filter(industry => 
          industry !== 'Total_Nonfarm' && industry !== 'Total_Private'
        );
      default:
        return allIndustries;
    }
  }, [indexedData, selectedView]);

  // Rest of the Y-axis domain calculation remains the same
  const yDomain = useMemo(() => {
    if (indexedData.length === 0) return [90, 110];
    const allValues = indexedData.flatMap(item => 
      Object.entries(item)
        .filter(([key]) => filteredIndustries.includes(key))
        .map(([_, value]) => value)
    );
    const min = Math.min(...allValues);
    const max = Math.max(...allValues);
    return [Math.floor(min - 2), Math.ceil(max + 2)];
  }, [indexedData, filteredIndustries]);

  const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#ff99cc'];

    // Add parseDate function
  const parseDate = (dateString) => {
    const [year, month] = dateString.split('-');
    const date = new Date(year, month - 2);
    // Add one month to correct the display
    date.setMonth(date.getMonth() + 1);
    return date;
  };
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    // Sort payload by value in descending order
    const sortedPayload = [...payload].sort((a, b) => b.value - a.value);

    return (
      <div className="bg-white p-4 border rounded shadow">
        <p className="font-semibold mb-2">
          {parseDate(label).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </p>
        {sortedPayload.map((entry, index) => (
          <div 
            key={index} 
            className="flex items-center gap-2 text-sm"
            style={{ color: entry.color }}
          >
            <div className="font-medium">
              {entry.name.replace(/_/g, ' ')}:
            </div>
            <div>
              {entry.value.toFixed(1)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      <IndustryMetricSelector selectedView={selectedView} setSelectedView={setSelectedView} />
      <YearSelector 
        timeFrame={timeFrame} 
        setTimeFrame={setTimeFrame} 
        options={[
          { value: 1, label: '1 Month' },
          { value: 6, label: '6 Months' },
          { value: 12, label: '1 Year' },
          { value: 60, label: '5 Years' }
        ]}
      />
      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={indexedData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="month"
            tickFormatter={(tick) => {
              const date = parseDate(tick);
              return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
            }}
          />
          <YAxis 
            domain={yDomain}
            label={{ value: 'Index (Base Period = 100)', angle: -90, position: 'insideLeft' }} 
          />
          <Tooltip content={<CustomTooltip />} />
          {filteredIndustries.map((industry, index) => (
            <Line
              key={industry}
              type="monotone"
              dataKey={industry}
              stroke={colors[index % colors.length]}
              name={industry.replace(/_/g, ' ')}
              dot={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};
